import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useDateFnsLocale } from "@/lib/format-dates";
import {
  formatOrderNumber,
  type ClientOrderStatusRow,
  type OrderThreadLastMessage,
} from "@threadheads/shared";
import { ClientOrderStatusChip } from "./ClientOrderStatusChip";
import { ClientOrderTypeBadge } from "./ClientOrderTypeBadge";
import { CLIENT_ORDER_STATUS_LABEL_KEY } from "../utils/status-style";

interface ClientOrderCardProps {
  order: ClientOrderStatusRow;
  selected: boolean;
  /** Orders without a Shopify id have no detail view — rendered muted and
   *  non-interactive, mirroring the previous table's non-clickable rows. */
  selectable: boolean;
  onSelect: () => void;
  /** Unread order-message count (Phase 3) — bolds the card + shows the badge. */
  unreadCount?: number;
  /** Latest thread message for the one-line preview (Phase 3). */
  lastMessage?: OrderThreadLastMessage | null;
}

const EMPTY_VALUE = "-";

/**
 * BLU-665 — one order in the inbox list pane (Layout C). A full-width button
 * (keyboard/AT actionable) showing order #, date, customer, item count, and
 * the colour-coded status chip. The selected card carries the accent left bar
 * + soft accent fill from the approved mockup. Tracking shows as plain text —
 * the copy affordance lives in the detail pane (no nested buttons).
 */
export function ClientOrderCard({
  order,
  selected,
  selectable,
  onSelect,
  unreadCount = 0,
  lastMessage = null,
}: ClientOrderCardProps) {
  const { t } = useTranslation("clientOrders");
  const unread = unreadCount > 0;
  const dateLocale = useDateFnsLocale();

  return (
    <button
      type="button"
      data-order-card
      disabled={!selectable}
      aria-current={selected ? "true" : undefined}
      onClick={selectable ? onSelect : undefined}
      className={cn(
        "relative flex w-full flex-col gap-1.5 border-b px-4 py-3 text-left",
        "transition-colors duration-200 motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        selected ? "bg-primary/5" : selectable && "hover:bg-muted/50",
        !selectable && "cursor-default opacity-70",
      )}
      aria-label={
        (unread
          ? t("card.ariaLabelUnread", {
              number: order.orderNumber ? formatOrderNumber(order.orderNumber) : EMPTY_VALUE,
              status: t(CLIENT_ORDER_STATUS_LABEL_KEY[order.clientStatus]),
              count: unreadCount,
            })
          : t("card.ariaLabel", {
              number: order.orderNumber ? formatOrderNumber(order.orderNumber) : EMPTY_VALUE,
              status: t(CLIENT_ORDER_STATUS_LABEL_KEY[order.clientStatus]),
            }))
      }
    >
      {selected ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-0.5 bg-primary"
        />
      ) : null}

      <div className="flex w-full items-center gap-2">
        {unread ? (
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
          />
        ) : null}
        <span
          className={cn(
            "truncate text-[13px] text-primary-strong",
            unread ? "font-bold" : "font-semibold",
          )}
        >
          {order.orderNumber ? formatOrderNumber(order.orderNumber) : EMPTY_VALUE}
        </span>
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground tabular-nums">
          {order.createdAt
            ? format(new Date(order.createdAt), "d MMM", { locale: dateLocale })
            : EMPTY_VALUE}
        </span>
        {unread ? (
          <span
            aria-label={t("card.unreadAria", { count: unreadCount })}
            className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-primary-strong px-1.5 text-[11px] font-bold text-primary-foreground tabular-nums"
          >
            {unreadCount}
          </span>
        ) : null}
      </div>

      <div className="flex w-full items-baseline gap-1.5">
        <span className="truncate text-sm">
          {order.customerName ?? EMPTY_VALUE}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          · {t("card.items", { count: order.itemCount })}
        </span>
      </div>

      <div className="flex w-full flex-wrap items-center gap-1.5">
        <ClientOrderStatusChip status={order.clientStatus} />
        {order.orderType ? (
          <ClientOrderTypeBadge type={order.orderType} />
        ) : null}
        {(order.tags ?? []).slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="inline-flex max-w-[120px] items-center truncate rounded-[5px] border bg-muted px-1.5 py-px text-[11px] font-medium text-muted-foreground"
          >
            {tag}
          </span>
        ))}
        {order.trackingNumber ? (
          <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground">
            {order.trackingNumber}
          </span>
        ) : null}
      </div>

      {lastMessage ? (
        <p
          className={cn(
            "w-full truncate text-xs",
            unread ? "font-medium text-foreground" : "text-muted-foreground",
          )}
        >
          <span className="font-semibold text-muted-foreground">
            {lastMessage.side === "client"
              ? t("card.previewYou")
              : (lastMessage.authorName ?? t("card.previewPartner"))}
            :
          </span>{" "}
          {lastMessage.preview || t("card.previewAttachment")}
        </p>
      ) : null}
    </button>
  );
}
